import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { 
  getClientesSinPrestamo, 
  getEstadisticasClientesSinPrestamo,
  getClientesSinPrestamoCompletos 
} from '../../../js/clientesSinPrestamoService';
import ClientesSinPrestamoFilters from '../../../components/ui/Admin/ClientesSinPrestamoComponents/ClientesSinPrestamoFilters';
import ClientesSinPrestamoTable from '../../../components/ui/Admin/ClientesSinPrestamoComponents/ClientesSinPrestamoTable';
import ClientesSinPrestamoKPICards from '../../../components/ui/Admin/ClientesSinPrestamoComponents/ClientesSinPrestamoKPICards';
import ReporteClientesSinPrestamo from '../../../components/ui/Admin/ClientesSinPrestamoComponents/ReporteClientesSinPrestamo';
import {
  SkeletonFilters,
  SkeletonInfoBanner,
  SkeletonClientesTable,
  EmptyState,
  ErrorState,
} from '../../../components/ui/Admin/ClientesSinPrestamoComponents/LoadingSkeletons/ClientesSinPrestamoSkeletons';
import ReporteClientesSinPrestamoExcel from '../../../components/ui/Admin/ClientesSinPrestamoComponents/ReporteClientesSinPrestamoExcel';
import Paginacion from '../../../components/Reutilizables/Paginacion';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';

const INITIAL_FILTERS = {
  search: '',
  estado_cliente: 'todos',
  per_page: 50,
  page: 1,
};

const INITIAL_STATE = {
  loading: false,
  error: '',
  data: [],
  meta: {},
  estadisticas: null,
  initialLoad: true,
};

export default function ClientesSinPrestamoUI() {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [uiErrors, setUiErrors] = useState({});
  const [serverErrorMsg, setServerErrorMsg] = useState('');
  const [state, setState] = useState(INITIAL_STATE);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);

  const pdfDataRef = useRef({ clientes: [], url: null, isGenerating: false });
  const excelDataRef = useRef({ clientes: [], isGenerating: false });
  const meta = useMemo(() => state?.meta ?? {}, [state?.meta]);
  const data = useMemo(() => state?.data ?? [], [state?.data]);
  const estadisticas = useMemo(() => state?.estadisticas ?? null, [state?.estadisticas]);
  const hasData = data.length > 0;
  const isInitialLoading = state.loading && state.initialLoad && !hasData;
  const hasError = Boolean(state.error);

  const handleValidationError = useCallback((payload) => {
    const backendErrors = payload?.errors || {};
    const mappedErrors = Object.keys(backendErrors).reduce((acc, key) => {
      if (backendErrors[key]?.length) {
        acc[key] = backendErrors[key][0];
      }
      return acc;
    }, {});

    setUiErrors(mappedErrors);
    setServerErrorMsg(payload?.message || 'Revise los campos marcados.');
  }, []);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      initialLoad: isInitialLoad
    }));
    setServerErrorMsg('');

    try {
      const [clientesResult, estadisticasResult] = await Promise.all([
        getClientesSinPrestamo(filters),
        getEstadisticasClientesSinPrestamo(),
      ]);

      setState({
        loading: false,
        error: '',
        data: Array.isArray(clientesResult?.data) ? clientesResult.data : [],
        meta: clientesResult?.meta ?? {},
        estadisticas: estadisticasResult,
        initialLoad: false,
      });
      setUiErrors({});
    } catch (error) {
      const status = error?.status || error?.response?.status;
      const payload = error?.response?.data || {};

      if (status === 422) {
        handleValidationError(payload);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          initialLoad: false
        }));
        return;
      }

      setState((prevState) => ({
        ...prevState,
        loading: false,
        error: error?.message || 'No se pudo cargar el reporte de clientes.',
        initialLoad: false,
      }));
    }
  }, [filters, handleValidationError]);

  useEffect(() => {
    fetchData(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!state.initialLoad) {
      fetchData(false);
    }
  }, [filters.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
    setUiErrors((prev) => ({ ...prev, [name]: '' }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setUiErrors({});
    setServerErrorMsg('');
    fetchData(true);
  }, [fetchData]);

  const handleResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setUiErrors({});
    setServerErrorMsg('');
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleGenerateExcel = useCallback(async () => {
    setIsLoadingExport(true);
    excelDataRef.current.isGenerating = true;
    
    try {
      const excelFilters = {
        search: filters.search,
        estado_cliente: filters.estado_cliente,
      };

      const result = await getClientesSinPrestamoCompletos(excelFilters);
      const clientes = Array.isArray(result?.data) ? result.data : [];
      
      if (clientes.length === 0) {
        setState((prevState) => ({
          ...prevState,
          error: 'No hay datos para generar el Excel.',
        }));
        excelDataRef.current.isGenerating = false;
        setIsLoadingExport(false);
        return;
      }

      excelDataRef.current = {
        clientes,
        isGenerating: true
      };
      
      setIsLoadingExport(false);
    } catch (error) {
      console.error('Error al generar Excel:', error);
      setState((prevState) => ({
        ...prevState,
        error: 'Error al generar el archivo Excel. Intente nuevamente.',
      }));
      excelDataRef.current.isGenerating = false;
      setIsLoadingExport(false);
    }
  }, [filters.search, filters.estado_cliente]);

  const handleExcelGenerated = useCallback(() => {
    excelDataRef.current = { clientes: [], isGenerating: false };
  }, []);

  const handleClosePDFModal = useCallback(() => {
    if (pdfDataRef.current.url) {
      URL.revokeObjectURL(pdfDataRef.current.url);
    }
    pdfDataRef.current = { clientes: [], url: null, isGenerating: false };
    setShowPDFModal(false);
  }, []);

  const handleGeneratePDF = useCallback(async () => {
    setIsLoadingExport(true);
    try {
      const pdfFilters = {
        search: filters.search,
        estado_cliente: filters.estado_cliente,
      };

      const result = await getClientesSinPrestamoCompletos(pdfFilters);
      const clientesCompletos = Array.isArray(result?.data) ? result.data : [];

      pdfDataRef.current = {
        clientes: clientesCompletos,
        url: null,
        isGenerating: true
      };
      
      setShowPDFModal(true);
      setIsLoadingExport(false);
    } catch (error) {
      console.error('Error al cargar clientes para PDF:', error);
      setState((prevState) => ({
        ...prevState,
        error: 'Error al cargar los datos para el PDF. Intente nuevamente.',
      }));
      setIsLoadingExport(false);
    }
  }, [filters.search, filters.estado_cliente]);

  const handlePDFGenerated = useCallback((url) => {
    pdfDataRef.current.url = url;
    pdfDataRef.current.isGenerating = false;
    setShowPDFModal(false);
    setTimeout(() => {
      setShowPDFModal(true);
    }, 0);
  }, []);

  const handleDismissError = useCallback(() => {
    setState((prevState) => ({ ...prevState, error: '' }));
  }, []);

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <header className="mb-6">
            <div className="h-8 w-80 bg-gray-200 rounded mb-2 animate-pulse"></div>
            <div className="h-4 w-60 bg-gray-200 rounded animate-pulse"></div>
          </header>
          <SkeletonFilters />
          <SkeletonInfoBanner />
          <SkeletonClientesTable />
        </div>
      </div>
    );
  }

  if (hasError && !hasData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorState
          title="Error al cargar el reporte"
          message={state.error}
          onRetry={() => fetchData(true)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {state.loading && !state.initialLoad && <FetchWithGif />}
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <section className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur">
          <div className="absolute -right-24 -top-24 h-48 w-48 rounded-full bg-rose-100/60" />
          <div className="absolute -left-20 -bottom-24 h-56 w-56 rounded-full bg-red-100/60" />
          <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Reportes</p>
              <h1 className="mt-2 text-2xl font-bold text-gray-900">
                Clientes sin préstamo activo
              </h1>
              <div className="mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-red-500 via-rose-500 to-red-600" />
              <p className="mt-3 text-sm text-gray-600">
                Listado de clientes sin préstamos activos.
              </p>
            </div>
            {hasData && (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleGenerateExcel}
                  disabled={state.loading || isLoadingExport}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                >
                  {isLoadingExport ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Generar Excel
                    </>
                  )}
                </button>
                <button
                  onClick={handleGeneratePDF}
                  disabled={state.loading || isLoadingExport}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                >
                  {isLoadingExport ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Generar PDF
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Filtros */}
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <ClientesSinPrestamoFilters
            filters={filters}
            onChange={handleFilterChange}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            loading={state.loading}
            errors={uiErrors}
            formError={serverErrorMsg}
          />
        </section>

        {/* Tarjetas KPI de Estadísticas */}
        {estadisticas && <ClientesSinPrestamoKPICards estadisticas={estadisticas} />}

        {/* Contenido principal: Tabla o Empty State */}
        {!hasData && !state.loading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 shadow-sm">
            <EmptyState
              title="No hay clientes sin préstamo activo"
              description="No se encontraron clientes con los filtros seleccionados"
              actionButton={
                <button
                  onClick={handleResetFilters}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition transform hover:scale-105"
                >
                  Ver todos los clientes
                </button>
              }
            />
          </div>
        ) : (
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <ClientesSinPrestamoTable
              rows={data}
              loading={state.loading}
              error={state.error}
            />
          </div>
        )}

        {/* Paginación */}
        {meta.total > 0 && meta.last_page > 1 && (
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <Paginacion
              paginacion={{
                pagina_actual: meta.current_page,
                ultima_pagina: meta.last_page,
                desde: meta.from,
                hasta: meta.to,
                total: meta.total
              }}
              alCambiarPagina={handlePageChange}
              cargando={state.loading}
              nombreItem="clientes"
              colorTema="red"
            />
          </div>
        )}

        {/* Información de filtros aplicados */}
        {hasData && filters.estado_cliente !== 'todos' && (
          <div className="mt-4 text-center text-sm text-gray-600">
            Mostrando solo: <strong>
              {filters.estado_cliente === 'sin_prestamo_activo' ? 'Con historial' : 'Sin registro de préstamos'}
            </strong>
          </div>
        )}

        {/* Toast de error */}
        {hasError && hasData && (
          <div className="fixed bottom-4 right-4 z-50 max-w-sm">
            <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">Error al actualizar</p>
                <p className="text-xs opacity-90">{state.error}</p>
              </div>
              <button
                onClick={handleDismissError}
                className="text-white hover:bg-red-700 rounded p-1"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Modal de PDF */}
        {showPDFModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-11/12 h-5/6 max-w-6xl flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold text-gray-800">
                  Reporte de Clientes sin Préstamo Activo
                </h2>
                <button
                  onClick={handleClosePDFModal}
                  className="text-gray-600 hover:text-gray-900 transition"
                  aria-label="Cerrar"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                {pdfDataRef.current.url ? (
                  <iframe
                    src={pdfDataRef.current.url}
                    title="Vista previa PDF"
                    className="w-full h-full"
                  />
                ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <svg className="animate-spin h-12 w-12 text-red-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      <p className="text-gray-600">Generando PDF...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Componente de generación de PDF */}
        {showPDFModal && pdfDataRef.current.isGenerating && pdfDataRef.current.clientes.length > 0 && (
          <ReporteClientesSinPrestamo
            clientes={pdfDataRef.current.clientes}
            filters={filters}
            estadisticas={estadisticas}
            onPDFGenerated={handlePDFGenerated}
          />
        )}

        {/* Componente de generación de Excel (oculto, solo genera y descarga) */}
        {excelDataRef.current.isGenerating && (
          <ReporteClientesSinPrestamoExcel
            clientes={excelDataRef.current.clientes}
            onComplete={handleExcelGenerated}
          />
        )}
      </div>
    </div>
  );
}
